import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue';

type SplitPair = {
    plantingId: string | null;
    tempPlantingKey: string | null;
    agriFieldId: string;
    label: string;
    area: number | null;
};

type PairShare = {
    plantingId: string | null;
    tempPlantingKey?: string | null;
    agriFieldId: string;
    usageQty: number | null;
    amount: number | null;
};

type SplitRow = SplitPair & {
    usageQty: number | null;
    amount: number | null;
};

type SortColumn = 'label' | 'area' | 'usageQty' | 'amount';

@Component({
    components: {
        Modal,
    }
})
export default class FertilizerPairSplitModal extends Vue {

    @Prop()
    showFertilizerPairSplitModal!: boolean

    @Prop()
    pairs!: SplitPair[];

    @Prop()
    fertilizerName!: string | null;

    @Prop()
    initialShares!: PairShare[] | null;

    @Prop()
    totalUsageQty!: number | null;

    @Prop()
    totalAmount!: number | null;

    @Prop()
    usageUnit!: string | null;

    @Prop({ default: true })
    usageEditable!: boolean;

    rows: SplitRow[] = [];
    altered = false;
    sortColumn: SortColumn | null = null;
    sortAsc = true;

    @Watch('showFertilizerPairSplitModal')
    onModalOpen(val: boolean) {
        if (!val) return;
        this.altered = false;
        this.sortColumn = null;
        this.sortAsc = true;
        this.rows = this.buildRows(this.initialShares);
    }

    toggleSort(column: SortColumn) {
        if (this.sortColumn === column) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortColumn = column;
            this.sortAsc = true;
        }
    }

    get sortedRows(): SplitRow[] {
        if (!this.sortColumn) return this.rows;
        const column = this.sortColumn;
        const dir = this.sortAsc ? 1 : -1;
        return [...this.rows].sort((a, b) => {
            const av = a[column];
            const bv = b[column];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'string' || typeof bv === 'string') {
                return String(av).localeCompare(String(bv), 'ja') * dir;
            }
            return (av - bv) * dir;
        });
    }

    private shareKey(plantingId: string | null | undefined, tempPlantingKey: string | null | undefined, agriFieldId: string | null | undefined): string {
        return `${plantingId ?? tempPlantingKey ?? ''}_${agriFieldId ?? ''}`;
    }

    private buildRows(shares: PairShare[] | null): SplitRow[] {
        const count = this.pairs.length;
        if (count === 0) return [];
        const byKey = new Map((shares || []).map(s => [this.shareKey(s.plantingId, s.tempPlantingKey, s.agriFieldId), s]));
        const hasAnyShares = !!shares && shares.length > 0;

        if (hasAnyShares) {
            return this.pairs.map(p => {
                const share = byKey.get(this.shareKey(p.plantingId, p.tempPlantingKey, p.agriFieldId));
                return { ...p, usageQty: share ? share.usageQty : null, amount: share ? share.amount : null };
            });
        }

        return this.freshSplit();
    }

    private freshSplit(): SplitRow[] {
        const usageShares = this.usageEditable
            ? this.splitByArea(this.totalUsageQty, 2)
            : this.pairs.map(() => this.totalUsageQty);
        const amountShares = this.splitByArea(this.totalAmount, 0);
        return this.pairs.map((p, i) => ({ ...p, usageQty: usageShares[i], amount: amountShares[i] }));
    }

    reapportion() {
        if (!window.confirm('明細の内容を破棄して、再按分します。よろしいですか？')) return;
        this.rows = this.freshSplit();
        this.altered = true;
    }

    private splitByArea(totalOrNull: number | null, decimals: number): number[] {
        const count = this.pairs.length;
        if (totalOrNull == null || count === 0) return this.pairs.map(() => null as unknown as number);
        const total: number = totalOrNull;
        const factor = Math.pow(10, decimals);
        const areas = this.pairs.map(p => p.area);
        const useArea = areas.every(a => a != null && a > 0);
        let totalArea = 0;
        if (useArea) {
            for (const a of areas) totalArea += a as number;
        }
        const shares = this.pairs.map((_, i) => {
            const raw = useArea ? (total * (areas[i] as number)) / totalArea : total / count;
            return Math.floor(raw * factor) / factor;
        });
        const sum = shares.reduce((s, v) => s + v, 0);
        const remainder = Math.round((total - sum) * factor) / factor;
        shares[0] = Math.round((shares[0] + remainder) * factor) / factor;
        return shares;
    }

    alteredForm() {
        this.altered = true;
    }

    get computedTotalUsage(): number {
        return this.rows.reduce((sum, r) => sum + (r.usageQty ?? 0), 0);
    }

    get computedTotalAmount(): number {
        return this.rows.reduce((sum, r) => sum + (r.amount ?? 0), 0);
    }

    get showAmountColumn(): boolean {
        return !!this.totalAmount;
    }

    /** Mirrors the usage modal's own unit display: append /10a in rate mode, matching how usageQty is a per-10a rate there. */
    get usageUnitDisplay(): string | null {
        if (!this.usageUnit) return this.usageUnit;
        return this.usageEditable ? this.usageUnit : `${this.usageUnit}/10a`;
    }

    formatNumberWithCommas = (v: number | null) => (v == null ? '---' : (v % 1 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toLocaleString()));

    validateNum(row: SplitRow, field: 'usageQty' | 'amount') {
        const value = row[field];
        if (value === '' as any || value === null || value === undefined) {
            row[field] = null;
            return;
        }
        const num = Number(value);
        row[field] = isNaN(num) ? null : num;
    }

    confirm() {
        const errors: string[] = [];
        const regex = /^\d{1,7}(\.\d{1,2})?$/;
        const re = /^\d{1,10}$/;

        if (this.usageEditable) {
            this.rows.forEach((row, index) => {
                const usageQtyStr = row.usageQty != null ? row.usageQty.toString().trim() : '';
                if (usageQtyStr === '') {
                    errors.push(`${index + 1}件目: 使用量を入力してください。`);
                } else if (!regex.test(usageQtyStr) || Number(usageQtyStr) <= 0) {
                    errors.push(`${index + 1}件目: 使用量は0より大きい整数部7桁、小数部2桁までの数値を入力してください。`);
                }
            });
        }
        const totalUsageQty = this.totalUsageQty;
        if (totalUsageQty != null) {
            if (this.usageEditable) {
                if (Math.round(this.computedTotalUsage * 100) !== Math.round(totalUsageQty * 100)) {
                    errors.push(`使用量の合計(${this.formatNumberWithCommas(this.computedTotalUsage)})が、使用量欄に入力した値(${this.formatNumberWithCommas(totalUsageQty)})と一致しません。`);
                }
            } else if (!this.rows.every(r => Math.round((r.usageQty ?? 0) * 100) === Math.round(totalUsageQty * 100))) {
                errors.push('使用量が全ペアで一致していません。再按分を行うか、明細を確認してください。');
            }
        }
        this.rows.forEach((row, index) => {
            const amountStr = row.amount != null ? row.amount.toString().trim() : '';
            if (this.showAmountColumn && amountStr === '') {
                errors.push(`${index + 1}件目: 金額を入力してください。`);
            } else if (amountStr !== '' && !re.test(amountStr)) {
                errors.push(`${index + 1}件目: 金額は10桁以内で入力してください。`);
            }
        });
        const totalAmount = this.totalAmount;
        if (totalAmount != null && Math.round(this.computedTotalAmount) !== Math.round(totalAmount)) {
            errors.push(`金額の合計(${this.formatNumberWithCommas(this.computedTotalAmount)})が、金額欄に入力した値(${this.formatNumberWithCommas(totalAmount)})と一致しません。`);
        }
        if (errors.length > 0) {
            window.alert(errors.join('\n'));
            return;
        }
        this.$emit('update:showFertilizerPairSplitModal', false);
        this.$emit('save', {
            shares: this.rows.map(r => ({ plantingId: r.plantingId, tempPlantingKey: r.tempPlantingKey, agriFieldId: r.agriFieldId, usageQty: r.usageQty, amount: r.amount })),
            totalUsageQty: this.totalUsageQty == null ? null : (this.usageEditable ? this.computedTotalUsage : this.totalUsageQty),
            totalAmount: this.totalAmount == null ? null : this.computedTotalAmount,
        });
    }

    close() {
        if (!this.altered || window.confirm("変更内容を破棄します。よろしいですか？")) {
            this.altered = false;
            this.$emit('update:showFertilizerPairSplitModal', false);
        }
    }

    cancelForm() {
        if (!this.altered) return;
        if (!window.confirm("変更内容を破棄します。よろしいですか？")) return;
        this.altered = false;
        this.rows = this.buildRows(this.initialShares);
    }
}
